/* eslint-disable max-len */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DataEntryMode } from '../../types/DataEntryMode';
import { GradesAndPromotionsFormViewModel } from '../../types/GradesAndPromotionsFormViewModel';
import { GradesAndPromotionsModel } from '../../types/GradesAndPromotionsModel';
import { ReactSelectOption } from '../../types/ReactSelectOption';
import { isEmptyObject } from '../../utilities/helperUtilities';
import type { RootState } from '../store';
import {
  fetchGradesAndPromotionsById,
  fetchGradesAndPromotionsFormViewModel,
  fetchGradesAndPromotionsSchoolYears,
  fetchStudentByMSISId,
} from '../thunks/gradesAndPromotionsThunks';

interface GradesAndPromotionsState {
  schoolYear: string;

  loadingGradesAndPromotions: boolean;
  gradesAndPromotions: GradesAndPromotionsModel;

  loadingFormViewModel: boolean;
  formViewModel: GradesAndPromotionsFormViewModel;

  dataEntryMode: DataEntryMode;

  // Search-related state
  searchError: string;
  hasSearchMatch: boolean;
  searchResults: GradesAndPromotionsModel;
}

export const defaultGradesAndPromotionsState: GradesAndPromotionsModel = {
  id: '',
  schoolYear: '',
  studentIdentifier: '',
  studentFirstName: '',
  studentLastOrSurname: '',
  studentMiddleName: '',
  birthDate: '',
  districtName: '',
  leaIdentifier: '',
  schoolName: '',
  schoolId: '',
  promotionStatus: '',
  highSchoolDiplomaType: '',
  diplomaOrCredentialAwardDate: '',
  entryGradeLevel: '',
  enrollmentEntryDate: '',
  updatedByName: '',
  updatedDate: '',
  studentUpdate: false,
  courses: [],
};

export const defaultFormState: GradesAndPromotionsFormViewModel = {
  defaultSchoolYear: '',
  defaultSchoolYearOption: { value: '', label: '' },
  selectedSchoolYearOption: { value: '', label: '' },
  schoolYearOptions: [],
  promotedOptions: [],
  updateReasonOptions: [],
  graduationTypeOptions: [],
};

export const defaultDataEntryMode = DataEntryMode.NEW;

export const initialState: GradesAndPromotionsState = {
  schoolYear: '',
  loadingGradesAndPromotions: false,
  gradesAndPromotions: defaultGradesAndPromotionsState,
  loadingFormViewModel: false,
  formViewModel: defaultFormState,
  dataEntryMode: defaultDataEntryMode,
  searchError: '',
  hasSearchMatch: false,
  searchResults: defaultGradesAndPromotionsState,
};

export const gradesAndPromotionsSlice = createSlice({
  name: 'gradesAndPromotions',
  initialState,
  reducers: {
    setSchoolYearFilter: (state, action: PayloadAction<string>) => {
      state.schoolYear = action.payload;
    },
    clearGradesAndPromotions: (state) => {
      state.gradesAndPromotions = defaultGradesAndPromotionsState;
    },
    saveGradesAndPromotions: (
      state,
      action: PayloadAction<GradesAndPromotionsModel>
    ) => {
      state.gradesAndPromotions = action.payload;
    },
    setGradesAndPromotionsDataEntryMode: (
      state,
      action: PayloadAction<DataEntryMode>
    ) => {
      state.dataEntryMode = action.payload;
    },
    setGradesAndPromotionsSchoolYear: (
      state,
      action: PayloadAction<ReactSelectOption>
    ) => {
      state.formViewModel.selectedSchoolYearOption = action.payload;
    },
    clearSearchError: (state) => {
      state.searchError = '';
    },
    setSearchError: (state, action: PayloadAction<string>) => {
      state.searchError = action.payload;
    },
    setHasSearchMatch: (state, action: PayloadAction<boolean>) => {
      state.hasSearchMatch = action.payload;
    },
    clearSearchResults: (state) => {
      state.searchResults = defaultGradesAndPromotionsState;
    },
  },
  extraReducers: (builder) => {
    // fetchGradesAndPromotionsById
    builder.addCase(fetchGradesAndPromotionsById.pending, (state) => {
      state.gradesAndPromotions = defaultGradesAndPromotionsState;
      state.loadingGradesAndPromotions = true;
    });
    builder.addCase(fetchGradesAndPromotionsById.fulfilled, (state, action) => {
      if (!isEmptyObject(action.payload)) {
        state.gradesAndPromotions = action.payload;
      }
      state.loadingGradesAndPromotions = false;
    });
    builder.addCase(fetchGradesAndPromotionsById.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingGradesAndPromotions = false;
      }
    });

    // fetchStudentByMSISId
    builder.addCase(fetchStudentByMSISId.pending, (state) => {
      state.searchResults = defaultGradesAndPromotionsState;
      state.loadingGradesAndPromotions = true;
      state.searchError = '';
      state.hasSearchMatch = false;
    });
    builder.addCase(fetchStudentByMSISId.fulfilled, (state, action) => {
      if (!isEmptyObject(action.payload)) {
        state.searchResults = action.payload;
        state.hasSearchMatch = true;
        state.searchError = '';
      }
      state.loadingGradesAndPromotions = false;
    });
    builder.addCase(fetchStudentByMSISId.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingGradesAndPromotions = false;
        state.hasSearchMatch = false;

        // Handle specific error cases
        if (action.error.message?.includes('409')) {
          state.searchError =
            'This student is not enrolled in the selected/assigned LEA and a school you are assigned to. Please check the MSIS ID and try again or reach out to your MSIS Primary/Secondary to verify permissions.';
        } else {
          state.searchError =
            'MSIS ID Not Found. Please ensure the MSIS ID has been created for the student.';
        }
      }
    });

    //fetchGradesAndPromotionsFormViewModel
    builder.addCase(fetchGradesAndPromotionsFormViewModel.pending, (state) => {
      state.formViewModel = defaultFormState;
      state.loadingFormViewModel = true;
    });
    builder.addCase(
      fetchGradesAndPromotionsFormViewModel.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload)) {
          state.formViewModel = action.payload;
        }
        state.loadingFormViewModel = false;
      }
    );
    builder.addCase(
      fetchGradesAndPromotionsFormViewModel.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingFormViewModel = false;
        }
      }
    );

    //fetch School Year options
    builder.addCase(fetchGradesAndPromotionsSchoolYears.pending, (state) => {
      state.formViewModel.schoolYearOptions = [];
      state.loadingFormViewModel = true;
    });
    builder.addCase(
      fetchGradesAndPromotionsSchoolYears.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload)) {
          state.formViewModel.schoolYearOptions = action.payload;
          const defaultValue = action.payload[0] || {
            value: '',
            label: '',
          };

          state.formViewModel.defaultSchoolYearOption = {
            value: defaultValue.value,
            label: defaultValue.text,
          };
          state.formViewModel.selectedSchoolYearOption = {
            value: defaultValue.value,
            label: defaultValue.text,
          };
        }
        state.loadingFormViewModel = false;
      }
    );
    builder.addCase(
      fetchGradesAndPromotionsSchoolYears.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingFormViewModel = false;
        }
      }
    );
  },
});

export const {
  setSchoolYearFilter,
  clearGradesAndPromotions,
  saveGradesAndPromotions,
  setGradesAndPromotionsDataEntryMode,
  setGradesAndPromotionsSchoolYear,
  clearSearchError,
  setSearchError,
  setHasSearchMatch,
  clearSearchResults,
} = gradesAndPromotionsSlice.actions;

export const selectSchoolYear = (state: RootState): string =>
  state.gradesAndPromotions.schoolYear;
// GradesAndPromotions
export const selectGradesAndPromotionsLoadingGradesAndPromotions = (
  state: RootState
): boolean => state.gradesAndPromotions.loadingGradesAndPromotions;
export const selectGradesAndPromotionsGradesAndPromotions = (
  state: RootState
): GradesAndPromotionsModel => state.gradesAndPromotions.gradesAndPromotions;

// formViewModel
export const selectGradesAndPromotionsLoadingFormViewModel = (
  state: RootState
): boolean => state.gradesAndPromotions.loadingFormViewModel;
export const selectGradesAndPromotionsFormViewModel = (
  state: RootState
): GradesAndPromotionsFormViewModel => state.gradesAndPromotions.formViewModel;

// dataEntryMode
export const selectGradesAndPromotionsDataEntryMode = (
  state: RootState
): DataEntryMode => state.gradesAndPromotions.dataEntryMode;

// Search-related selectors
export const selectGradesAndPromotionsSearchError = (
  state: RootState
): string => state.gradesAndPromotions.searchError;

export const selectGradesAndPromotionsHasSearchMatch = (
  state: RootState
): boolean => state.gradesAndPromotions.hasSearchMatch;

export const selectGradesAndPromotionsSearchResults = (
  state: RootState
): GradesAndPromotionsModel => state.gradesAndPromotions.searchResults;

export default gradesAndPromotionsSlice.reducer;
